import { createSlice } from '@reduxjs/toolkit';
import {
    fetchCoreSettings,
    fetchUserProfile,
    loginUser,
    loginWithGoogle,
    signupOtpVerification,
    signUpUser,
    sentLoginOtpCode,
    deliveryPartnerSignup,
    fetchUserPayments,
} from '../thunk/authThunk';
import { updatePickupDate } from '../thunk/userThunk';
import { getSecureItem, setSecureItem, removeSecureItem } from '../../utils/secureStorage';
const initialState = {
    user: null,
    token: getSecureItem('token') || null,
    authInitialized: false,
    loading: false,
    error: null,
    userInfo: {},
    userPayments: {},
    userPaymentsLoading: true,
    coreSettings: {},
};
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logoutUser: (state) => {
            // Clear auth state
            state.user = null;
            state.token = null;
            state.userInfo = {};
            state.authInitialized = false;

            // Remove secure token
            removeSecureItem('token');
        },
        updatePickupId: (state, action) => {
            if (state.userInfo) {
                state.userInfo.pickup_id = action.payload;
            }
        },
        updateDeliveryMethod: (state, action) => {
            if (state.userInfo) {
                state.userInfo.delivery_method = action.payload;
            }
        },
        updatePaymentMethod: (state, action) => {
            if (state.userInfo) {
                state.userInfo.payment_option = action.payload;
            }
        },
        updatePaypalEmail: (state, action) => {
            if (state.userInfo) {
                state.userInfo.paypal_email_id = action.payload;
            }
        },
        updatePaymentInfo: (state, action) => {
            if (state.userInfo) {
                state.userInfo.payment_selected_info = action.payload;
            }
        },
        updateSelectedCC: (state, action) => {
            if (state.userInfo) {
                state.userInfo.payment_card_id = action.payload;
            }
        },
        updateUserCCList: (state, action) => {
            if (state.userInfo) {
                state.userInfo.payment_card_info = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Login
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                //state.user = action.payload.user;
                state.token = action.payload.hal_token;

                // Store token securely
                if (action.payload.hal_token) {
                    setSecureItem('token', action.payload.hal_token);
                }
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // SignUp User
            .addCase(signUpUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(signUpUser.fulfilled, (state, action) => {
                state.loading = false;
                state.token = action.payload.hal_token;
            })
            .addCase(signUpUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // OTP Verification
            .addCase(signupOtpVerification.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(signupOtpVerification.fulfilled, (state, action) => {
                state.loading = false;
                state.token = action.payload.hal_token;

                // Store token securely
                if (action.payload.hal_token) {
                    setSecureItem('token', action.payload.hal_token);
                }
            })
            .addCase(signupOtpVerification.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Inside builder
            .addCase(sentLoginOtpCode.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(sentLoginOtpCode.fulfilled, (state, action) => {
                state.loading = false;
                // Optionally store info like phone_number or any token if needed
            })
            .addCase(sentLoginOtpCode.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Google Login
            .addCase(loginWithGoogle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginWithGoogle.fulfilled, (state, action) => {
                state.loading = false;
                state.token = action.payload.hal_token;

                // Store token securely
                if (action.payload.hal_token) {
                    setSecureItem('token', action.payload.hal_token);
                }
            })
            .addCase(loginWithGoogle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch User
            .addCase(fetchUserProfile.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload.data;
                state.authInitialized = true;
                state.userInfo = {
                    user_id: action?.payload?.data?.user_id,
                    user_name: action?.payload?.data?.name,
                    user_role: action?.payload?.data?.roles,
                    email: action?.payload?.data?.email,
                    billing_address: action?.payload?.data?.street_name,
                    delivery_method: action?.payload?.data?.delivery_method,
                    phone: action?.payload?.data?.phone_number,
                    payment_card_id: action?.payload?.data?.payment_card_id,
                    payment_option: action?.payload?.data?.payment_option,
                    order_count: action?.payload?.data?.order_count,
                    pickup_id: action?.payload?.data?.pickup_id,
                    pickup_date: action?.payload?.data?.pickup_date,
                    rewards_balance: action?.payload?.data?.available_rewards_points_amount || 0,
                    payment_selected_info: action?.payload?.data?.payment_selected_info || null,
                    is_verified: action?.payload?.data?.is_verified || false,
                };

                // Check if Super Admin cleared cache for this specific user
                const serverCacheVer = action.payload?.data?.cache_version || 0;
                const localCacheVer = Number(localStorage.getItem('hf_user_cache_ver') || 0);
                if (serverCacheVer > localCacheVer) {
                    localStorage.setItem('hf_user_cache_ver', serverCacheVer.toString());
                    localStorage.removeItem('hf_promoCode');
                    sessionStorage.clear();
                    window.location.reload();
                }
            })
            .addCase(fetchUserProfile.rejected, (state) => {
                state.loading = false;
                state.user = null;
                state.token = null;
                state.authInitialized = true;
                localStorage.removeItem('token');
            })
            .addCase(updatePickupDate.fulfilled, (state, action) => {
                const newDate = action.payload?.pickup_date;
                if (newDate) {
                    if (state.userInfo) {
                        state.userInfo.pickup_date = newDate;
                    }
                    if (state.user) {
                        state.user.pickup_date = newDate;
                    }
                }
            })
            .addCase(fetchCoreSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCoreSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.coreSettings = action.payload;
            })
            .addCase(fetchCoreSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delivery Partner Signup
            .addCase(deliveryPartnerSignup.pending, (state) => {
                state.partnerSignupLoading = true;
                state.error = null;
            })
            .addCase(deliveryPartnerSignup.fulfilled, (state, action) => {
                state.partnerSignupLoading = false;
                state.deliveryPartner = action.payload;
                state.token = action.payload.hal_token;
            })
            .addCase(deliveryPartnerSignup.rejected, (state, action) => {
                state.partnerSignupLoading = false;
                state.error = action.payload;
            })
            // User Payment Method
            .addCase(fetchUserPayments.pending, (state) => {
                state.userPaymentsLoading = true;
                state.error = null;
            })
            .addCase(fetchUserPayments.fulfilled, (state, action) => {
                state.userPaymentsLoading = false;
                state.userPayments = action.payload;
            })
            .addCase(fetchUserPayments.rejected, (state, action) => {
                state.userPaymentsLoading = false;
                state.error = action.payload;
            });
    },
});

// Need to export action and reducer
// Action
export const { logoutUser } = authSlice.actions;
// Reducer
export default authSlice.reducer;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthInitialized = (state) => state.auth.authInitialized;
export const selectAuthError = (state) => state.auth.error;
export const selectUserInfo = (state) => state.userInfo;
export const selectUserPayments = (state) => state.userPayments;
export const {
    updatePickupId,
    updateDeliveryMethod,
    updatePaymentMethod,
    updatePaypalEmail,
    updateSelectedCC,
    updateUserCCList,
    updatePaymentInfo,
} = authSlice.actions;
export const selectDeliveryPartner = (state) => state.auth.deliveryPartner;
export const selectPartnerSignupLoading = (state) => state.auth.partnerSignupLoading;
export const getAddressByPickupId = (state, pickupId, type) => {
    const { pickupList, homeList, charityList } = state;

    if (type === 'home') {
        return homeList.find((item) => item.pickup_id === pickupId) || null;
    } else if (type === 'charity') {
        return charityList.find((item) => item.pickup_id === pickupId) || null;
    } else {
        return pickupList.find((item) => item.pickup_id === pickupId) || null;
    }
};
